// Actualización del API para incluir nuevos endpoints

import axios from 'axios'

// Configuración base para axios
const apiClient = axios.create({
  baseURL: 'https://8jr6qclo81.execute-api.us-east-2.amazonaws.com/dev/api',
  timeout: 10000,
  // Eliminamos el 'Content-Type' de la configuración base para que no se aplique a GET
  headers: {
    'Accept': 'application/json'
  }
})

// Interceptor para agregar token y gestionar encabezados
apiClient.interceptors.request.use(
  config => {
    // Debug logging para shipments
    if (config.url && config.url.includes('shipments')) {
      console.log('🚀 API Request for shipments:', {
        url: config.url,
        method: config.method,
        baseURL: config.baseURL,
        fullURL: `${config.baseURL}${config.url}`
      })
    }

    // Si el método es GET, eliminamos el encabezado Content-Type
    if (config.method && config.method.toLowerCase() === 'get') {
      delete config.headers['Content-Type']
    } else {
      // Para otros métodos, se puede establecer el Content-Type
      config.headers['Content-Type'] = 'application/json'
    }
    
    // Interceptor para agregar token de autenticación
    const token = localStorage.getItem('token')
    if (token) {
      // Verificar si el token ya incluye el prefijo Bearer
      if (token.startsWith('Bearer ')) {
        config.headers.Authorization = token
      } else {
        config.headers.Authorization = `Bearer ${token}`
      }
      
      // Debug logging para shipments con token
      if (config.url && config.url.includes('shipments')) {
        console.log('🔑 Token found and added:', {
          token: token.substring(0, 20) + '...',
          authHeader: config.headers.Authorization.substring(0, 30) + '...'
        })
      }
    } else {
      // Debug logging para shipments sin token
      if (config.url && config.url.includes('shipments')) {
        console.log('⚠️ No token found in localStorage')
      }
    }
    
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Interceptor para manejar respuestas (opcional)
apiClient.interceptors.response.use(
  response => {
    // Debug logging para shipments
    if (response.config && response.config.url && response.config.url.includes('shipments')) {
      console.log('✅ API Response for shipments:', {
        status: response.status,
        dataLength: response.data?.length || 0,
        url: response.config.url,
        data: response.data
      })
    }
    return response
  },
  error => {
    // Debug logging para errores en shipments
    if (error.config && error.config.url && error.config.url.includes('shipments')) {
      console.error('❌ API Error for shipments:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config.url,
        message: error.message
      })
    }
    
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export default {
  // Métodos genéricos
  get(url, config = {}) {
    return apiClient.get(url, config)
  },
  post(url, data, config = {}) {
    return apiClient.post(url, data, config)
  },
  put(url, data, config = {}) {
    return apiClient.put(url, data, config)
  },
  delete(url, config = {}) {
    return apiClient.delete(url, config)
  },

  // Autenticación
  login(credentials) {
    return apiClient.post('/auth/login', credentials)
  },
  
  // Productos
  getProducts() {
    return apiClient.get('/products')
  },
  getProduct(id) {
    return apiClient.get(`/products/${id}`)
  },
  createProduct(product) {
    return apiClient.post('/products', product)
  },
  updateProduct(id, product) {
    return apiClient.put(`/products/${id}`, product)
  },
  deleteProduct(id) {
    return apiClient.delete(`/products/${id}`)
  },
  createBulkProducts(products) {
    return apiClient.post('/products/bulk', { products })
  },

  // Buscar producto por SKU (búsqueda exacta)
  searchProductBySku(sku) {
    return apiClient.get(`/products/search/sku/${sku}`)
  },

  // Buscar productos por SKU o nombre (búsqueda parcial)
  searchProductsBySku(query) {
    const url = `/products/search/sku?q=${encodeURIComponent(query)}`
    return apiClient.get(url)
  },
  
  // Categorías
  getCategories() {
    return apiClient.get('/categories')
  },
  createCategory(category) {
    return apiClient.post('/categories', category)
  },
  updateCategory(id, category) {
    return apiClient.put(`/categories/${id}`, category)
  },
  deleteCategory(id) {
    return apiClient.delete(`/categories/${id}`)
  },
  
  // Ubicaciones
  getLocations() {
    return apiClient.get('/locations')
  },
  getLocation(id) {
    return apiClient.get(`/locations/${id}`)
  },
  createLocation(location) {
    return apiClient.post('/locations', location)
  },
  updateLocation(id, location) {
    return apiClient.put(`/locations/${id}`, location)
  },
  deleteLocation(id) {
    return apiClient.delete(`/locations/${id}`)
  },
  
  // Inventario
  getInventory() {
    return apiClient.get('/inventory')
  },
  getInventoryItem(id) {
    return apiClient.get(`/inventory/${id}`)
  },
  adjustInventory(id, adjustment) {
    return apiClient.post(`/inventory/${id}/adjust`, adjustment)
  },
  getWarehouseInventory(warehouseId) {
    return apiClient.get(`/warehouses/${warehouseId}/inventory`)
  },
  
  // Transacciones
  getTransactions() {
    return apiClient.get('/transactions')
  },
  getTransaction(id) {
    return apiClient.get(`/transactions/${id}`)
  },
  createTransaction(transaction) {
    return apiClient.post('/transactions', transaction)
  },
  updateTransaction(id, transaction) {
    console.log('=== DEBUG API updateTransaction ===')
    console.log('id:', id)
    console.log('transaction:', transaction)
    console.log('transaction.details:', transaction.details)
    
    return apiClient.put(`/transactions/${id}`, transaction)
  },
  deleteTransaction(id) {
    return apiClient.delete(`/transactions/${id}`)
  },
  
  // Ingresos
  getIngresos() {
    return apiClient.get('/transactions/ingresos')
  },
  createIngreso(ingreso) {
    return apiClient.post('/transactions/ingresos', ingreso)
  },
  
  // Egresos
  getEgresos() {
    return apiClient.get('/transactions/egresos')
  },
  createEgreso(egreso) {
    return apiClient.post('/transactions/egresos', egreso)
  },
  
  // Trazabilidad
  getTraceability(productId) {
    return apiClient.get(`/traceability/${productId}`)
  },
  
  // Dashboard
  getDashboardData() {
    return apiClient.get('/dashboard')
  },
  
  getInventoryStats() {
    return apiClient.get('/inventory/stats')
  },
  
  getRecentActivities() {
    return apiClient.get('/activities/recent')
  },
  
  getLowStockProducts() {
    return apiClient.get('/inventory/low-stock')
  },
  
  getPendingTransactions() {
    return apiClient.get('/transactions/pending')
  },
  
  // Bodegas (Warehouses)
  getWarehouses() {
    return apiClient.get('/warehouses')
  },
  getWarehouse(id) {
    return apiClient.get(`/warehouses/${id}`)
  },
  createWarehouse(warehouse) {
    return apiClient.post('/warehouses', warehouse)
  },
  updateWarehouse(id, warehouse) {
    return apiClient.put(`/warehouses/${id}`, warehouse)
  },
  deleteWarehouse(id) {
    return apiClient.delete(`/warehouses/${id}`)
  },
  
  // Zonas (Zones)
  getZones() {
    return apiClient.get('/zones')
  },
  getWarehouseZones(warehouseId) {
    return apiClient.get(`/warehouses/${warehouseId}/zones`)
  },
  getZone(id) {
    return apiClient.get(`/zones/${id}`)
  },
  createZone(warehouseId, zone) {
    return apiClient.post(`/zones/warehouse/${warehouseId}`, zone)
  },
  updateZone(id, zone) {
    return apiClient.put(`/zones/${id}`, zone)
  },
  deleteZone(id) {
    return apiClient.delete(`/zones/${id}`)
  },
  
  // Capacidad de zonas
  getZoneCapacity(zoneId) {
    return apiClient.get(`/zones/${zoneId}/capacity`)
  },
  updateZoneCapacity(zoneId, capacityData) {
    return apiClient.put(`/zones/${zoneId}/capacity`, capacityData)
  },
  checkZoneCapacity(zoneId, quantityData) {
    return apiClient.post(`/zones/${zoneId}/check-capacity`, quantityData)
  },
  
  // Ubicaciones por zona
  getLocationsByZone(zoneId) {
    return apiClient.get(`/zones/${zoneId}/locations`)
  },
  
  // Generar códigos de barras para ubicaciones
  generateLocationBarcodes() {
    return apiClient.post('/intelligent-storage/generate-barcodes')
  },
  
  // Ruta más corta (Shortest Path)
  calculateShortestPath(data) {
    return apiClient.post('/shortest-path', data)
  },

  // Calcular categorías ABC
  calculateABCCategories() {
    return apiClient.post('/analytics/calculate-abc-categories')
  },

  // Analytics
  getKPIs(filters) {
    return apiClient.get('/analytics/kpis', { params: filters })
  },

  getABCAnalysis(filters) {
    return apiClient.get('/analytics/abc-analysis', { params: filters })
  },

  getDINAnalysis(filters) {
    return apiClient.get('/analytics/din-analysis', { params: filters })
  },

  getLowStockProducts(filters) {
    return apiClient.get('/analytics/low-stock', { params: filters })
  },

  getRotationAnalysis(params) {
    return apiClient.get('/analytics/rotation-analysis', { params })
  },

  getDemandPredictions(filters) {
    return apiClient.get('/analytics/demand-predictions', { params: filters })
  },

  recalculatePredictions(filters) {
    return apiClient.post('/analytics/recalculate-predictions', filters)
  },

  exportAnalytics(params) {
    return apiClient.get('/analytics/export', { params })
  },

  // ==================== ORDERS MANAGEMENT ====================
  
  // Órdenes
  getOrders(params) {
    return apiClient.get('/orders', { params })
  },
  getOrder(id) {
    return apiClient.get(`/orders/${id}`)
  },
  createOrder(order) {
    return apiClient.post('/orders', order)
  },
  updateOrder(id, order) {
    return apiClient.put(`/orders/${id}`, order)
  },
  
  // Clientes
  getCustomers() {
    return apiClient.get('/orders/customers')
  },
  searchCustomers(query) {
    return apiClient.get('/orders/customers/search', { params: { q: query } })
  },
  getCustomer(id) {
    return apiClient.get(`/orders/customers/${id}`)
  },
  createCustomer(customer) {
    return apiClient.post('/orders/customers', customer)
  },
  updateCustomer(id, customer) {
    return apiClient.put(`/orders/customers/${id}`, customer)
  },
  
  // Productos con inventario
  getProductsWithInventory() {
    return apiClient.get('/orders/products/inventory')
  },
  getProductLocations(productId) {
    return apiClient.get(`/orders/products/${productId}/locations`)
  },
  
  // Operaciones de órdenes
  allocateOrder(orderId, allocationData = null) {
    return apiClient.post(`/orders/${orderId}/allocate`, allocationData)
  },
  
  getOrderGranularCriteria(orderId) {
    return apiClient.get(`/orders/${orderId}/granular-criteria`)
  },
  releaseAllocations(orderId) {
    return apiClient.post(`/orders/${orderId}/release`)
  },
  createPickingList(orderId) {
    return apiClient.post(`/orders/${orderId}/picking-list`)
  },
  createShipment(orderId, shipmentData) {
    return apiClient.post(`/orders/${orderId}/ship`, shipmentData)
  },
  
  // Dashboard de órdenes
  getOrdersDashboardStats() {
    return apiClient.get('/orders/dashboard/stats')
  },
  
  // Picking Lists
  getPickingLists(params) {
    return apiClient.get('/orders/picking-lists', { params })
  },
  assignPickingList(listId, data) {
    return apiClient.post(`/orders/picking-lists/${listId}/assign`, data)
  },
  startPicking(listId) {
    return apiClient.post(`/orders/picking-lists/${listId}/start`)
  },
  completePickingList(listId) {
    return apiClient.post(`/orders/picking-lists/${listId}/complete`)
  },
  pickItem(itemId, data) {
    return apiClient.post(`/orders/picking-items/${itemId}/pick`, data)
  },
  
  // Envíos
  getShipments() {
    return apiClient.get('/shipments')
  },
  updateShipmentStatus(shipmentId, data) {
    return apiClient.put(`/shipments/${shipmentId}/track`, data)
  },

  // ==================== USUARIOS ====================
  
  // Obtener todos los usuarios
  getUsers() {
    return apiClient.get('/users')
  },
  
  // Obtener usuario específico
  getUser(id) {
    return apiClient.get(`/users/${id}`)
  },
  
  // Crear nuevo usuario
  createUser(userData) {
    return apiClient.post('/users', userData)
  },
  
  // Actualizar usuario
  updateUser(id, userData) {
    return apiClient.put(`/users/${id}`, userData)
  },
  
  // Eliminar usuario
  deleteUser(id) {
    return apiClient.delete(`/users/${id}`)
  },
  
  // Obtener solo operarios
  getOperators() {
    return apiClient.get('/users/operators')
  },
  
  // Obtener roles disponibles
  getUserRoles() {
    return apiClient.get('/users/roles')
  },

  // ==================== PICKING LISTS EXTENDIDO ====================
  
  // Obtener lista de picking específica con detalles completos
  getPickingList(id) {
    return apiClient.get(`/orders/picking-lists/${id}`)
  },
  
  // Iniciar picking list
  startPickingList(id) {
    return apiClient.post(`/orders/picking-lists/${id}/start`)
  },
  
  // Completar picking list
  completePickingList(id) {
    return apiClient.post(`/orders/picking-lists/${id}/complete`)
  },
  
  // Actualizar item de picking
  updatePickingItem(itemId, data) {
    return apiClient.put(`/orders/picking-items/${itemId}`, data)
  },
  
  // Escanear código de barras para picking piece-by-piece
  scanBarcodeForPicking(itemId, barcode) {
    return apiClient.post(`/orders/picking-items/${itemId}/scan-barcode`, { barcode })
  },

  // ==================== PICKING INTELIGENTE ====================
  
  // Tareas de picking
  getPickingTasks(params) {
    return apiClient.get('/intelligent-picking/tasks', { params })
  },
  
  getPickingTask(id) {
    return apiClient.get(`/intelligent-picking/tasks/${id}`)
  },
  
  createPickingTask(taskData) {
    return apiClient.post('/intelligent-picking/tasks', taskData)
  },
  
  startPickingTask(id) {
    return apiClient.post(`/intelligent-picking/tasks/${id}/start`)
  },
  
  completePickingTask(id) {
    return apiClient.post(`/intelligent-picking/tasks/${id}/complete`)
  },
  
  // Estrategias de picking
  getPickingStrategies() {
    return apiClient.get('/intelligent-picking/strategies')
  },
  
  // Optimización de rutas
  optimizePickingRoute(pickingListId, strategy = 'zone_weight_optimized') {
    return apiClient.post(`/intelligent-picking/optimize-route/${pickingListId}`, {
      strategy
    })
  },
  
  // Sugerencias de ruta
  getRouteSuggestions(pickingListId) {
    return apiClient.get(`/intelligent-picking/route-suggestions/${pickingListId}`)
  },
  
  // Prioridades de picking
  getPickingPriorities() {
    return apiClient.get('/intelligent-picking/priorities')
  },
  
  // Simular estrategias de picking
  simulatePickingStrategies(simulationData) {
    return apiClient.post('/intelligent-picking/simulate', simulationData)
  },
  
  // Crear tarea de picking inteligente (legacy - mantener compatibilidad)
  createIntelligentPickingTask(taskData) {
    return apiClient.post('/intelligent-picking/tasks', taskData)
  },
  
  // Actualizar estrategia de producto
  updateProductPickingStrategy(productId, strategyData) {
    return apiClient.put(`/intelligent-picking/product/${productId}/strategy`, strategyData)
  },

  // ==================== WAVE PICKING ====================
  
  // Crear ola de picking
  createPickingWave(waveData) {
    return apiClient.post('/wave-picking/create-wave', waveData)
  },
  
  // Analizar órdenes para wave picking
  analyzeOrdersForWave(orderData) {
    return apiClient.post('/wave-picking/analyze-orders', orderData)
  },
  
  // Sugerir configuración de olas
  suggestWaveConfiguration(criteria) {
    return apiClient.post('/wave-picking/suggest-wave', criteria)
  },
  
  // Obtener configuración de wave picking
  getWaveSettings() {
    return apiClient.get('/wave-picking/wave-settings')
  },
  
  // Actualizar configuración de wave picking
  updateWaveSettings(settings) {
    return apiClient.put('/wave-picking/wave-settings', settings)
  },

  // Gestión de Costos
  getCostStatistics(timeRange) {
    return apiClient.get(`/analytics/cost-statistics?time_range=${timeRange}`)
  },
  
  updateProductUnitPrice(productId, data) {
    return apiClient.put(`/analytics/products/${productId}/unit-price`, data)
  },

  // Sistema de Almacenamiento Inteligente
  createStorageList(data) {
    return apiClient.post('/intelligent-storage/storage-lists', data)
  },

  getStorageLists(params) {
    return apiClient.get('/intelligent-storage/storage-lists', { params })
  },

  getStorageList(id) {
    return apiClient.get(`/intelligent-storage/storage-lists/${id}`)
  },

  assignStorageList(id, data) {
    return apiClient.post(`/intelligent-storage/storage-lists/${id}/assign`, data)
  },

  startStorageList(id) {
    return apiClient.post(`/intelligent-storage/storage-lists/${id}/start`)
  },

  completeStorageList(id) {
    return apiClient.post(`/intelligent-storage/storage-lists/${id}/complete`)
  },

  scanStorageLocation(itemId, data) {
    return apiClient.post(`/intelligent-storage/storage-items/${itemId}/scan-location`, data)
  },

  scanStorageProduct(itemId, data) {
    return apiClient.post(`/intelligent-storage/storage-items/${itemId}/scan-product`, data)
  },

  scanStorageProductPiece(itemId, barcode) {
    return apiClient.post(`/intelligent-storage/storage-items/${itemId}/scan-product-piece`, { barcode })
  },

  // ==================== MOVIMIENTOS INTERNOS ====================
  
  // Obtener todos los movimientos internos
  getMovimientosInternos() {
    return apiClient.get('/transactions/movimientos-internos')
  },
  
  // Crear nuevo movimiento interno
  createMovimientoInterno(movementData) {
    return apiClient.post('/transactions/movimientos-internos', movementData)
  },
  
  // Ejecutar movimiento interno
  executeMovimientoInterno(movementId) {
    return apiClient.post(`/transactions/movimientos-internos/${movementId}/execute`)
  },
  
  // Actualizar movimiento interno
  updateMovimientoInterno(movementId, movementData) {
    return apiClient.put(`/transactions/${movementId}`, movementData)
  },
  
  // Eliminar movimiento interno
  deleteMovimientoInterno(movementId) {
    return apiClient.delete(`/transactions/${movementId}`)
  },

  // Despachos - Funcionalidades expandidas
  getDespachos() {
    return apiClient.get('/transactions/egresos') // Mantiene compatibilidad
  },
  
  createDespacho(despachoData) {
    return apiClient.post('/transactions/egresos', despachoData)
  },
  
  // Consolidación de órdenes
  getPickedOrders(filters = {}) {
    const params = new URLSearchParams(filters).toString()
    return apiClient.get(`/despachos/picked-orders?${params}`)
  },
  
  consolidateOrders(orderIds, consolidationData) {
    return apiClient.post('/despachos/consolidations', {
      order_ids: orderIds,
      consolidation_data: consolidationData
    })
  },
  
  getConsolidations(filters = {}) {
    const params = new URLSearchParams(filters).toString()
    return apiClient.get(`/despachos/consolidations?${params}`)
  },
  
  // Gestión de muelles de carga
  getLoadingDocks() {
    return apiClient.get('/despachos/loading-docks')
  },
  
  createLoadingDock(dockData) {
    return apiClient.post('/despachos/loading-docks', dockData)
  },
  
  updateLoadingDock(dockId, dockData) {
    return apiClient.put(`/despachos/loading-docks/${dockId}`, dockData)
  },
  
  deleteLoadingDock(dockId) {
    return apiClient.delete(`/despachos/loading-docks/${dockId}`)
  },
  
  assignOrdersToDock(dockId, orderIds) {
    return apiClient.post(`/despachos/loading-docks/${dockId}/assign`, {
      order_ids: orderIds
    })
  },
  
  removeOrderFromDock(dockId, orderId) {
    return apiClient.delete(`/despachos/loading-docks/${dockId}/orders/${orderId}`)
  },
  
  startDockLoading(dockId, loadingData = {}) {
    return apiClient.post(`/despachos/loading-docks/${dockId}/start-loading`, loadingData)
  },
  
  completeDockLoading(dockId, completionData = {}) {
    return apiClient.post(`/despachos/loading-docks/${dockId}/complete-loading`, completionData)
  },
  
  // Documentación de envíos
  getShipments(filters = {}) {
    const params = new URLSearchParams(filters).toString()
    return apiClient.get(`/despachos/shipments?${params}`)
  },
  
  createDespachoShipment(shipmentData) {
    return apiClient.post('/despachos/shipments', shipmentData)
  },
  
  updateShipment(shipmentId, shipmentData) {
    return apiClient.put(`/despachos/shipments/${shipmentId}`, shipmentData)
  },
  
  deleteShipment(shipmentId) {
    return apiClient.delete(`/despachos/shipments/${shipmentId}`)
  },
  
  // PDF endpoints
  downloadShipmentManifest(shipmentId) {
    return apiClient.get(`/despachos/shipments/${shipmentId}/download-manifest`, {
      responseType: 'blob'
    })
  },
  
  regenerateShipmentManifest(shipmentId) {
    return apiClient.post(`/despachos/shipments/${shipmentId}/regenerate-manifest`)
  },
  
  generateShippingLabel(shipmentId) {
    return apiClient.post(`/shipments/${shipmentId}/generate-label`, {}, {
      responseType: 'blob' // Para descargar PDF
    })
  },
  
  trackShipment(shipmentId) {
    return apiClient.get(`/shipments/${shipmentId}/tracking`)
  },
  
  updateShipmentStatus(shipmentId, statusData) {
    return apiClient.put(`/shipments/${shipmentId}/status`, statusData)
  },
  
  // Reportes y exportación de despachos
  exportDespachos(filters = {}) {
    const params = new URLSearchParams(filters).toString()
    return apiClient.get(`/transactions/egresos/export?${params}`, {
      responseType: 'blob'
    })
  },
  
  getDespachoStats(filters = {}) {
    const params = new URLSearchParams(filters).toString()
    return apiClient.get(`/transactions/egresos/stats?${params}`)
  },
  
  // Integración con transportistas
  getCarriers() {
    return apiClient.get('/carriers')
  },
  
  createCarrier(carrierData) {
    return apiClient.post('/carriers', carrierData)
  },
  
  updateCarrier(carrierId, carrierData) {
    return apiClient.put(`/carriers/${carrierId}`, carrierData)
  },
  
  getCarrierServices(carrierId) {
    return apiClient.get(`/carriers/${carrierId}/services`)
  },
  
  calculateShippingRates(shipmentData) {
    return apiClient.post('/carriers/calculate-rates', shipmentData)
  },

  // ==================== TRAZABILIDAD ====================
  
  // Obtener eventos de trazabilidad
  getTraceabilityEvents(params = {}) {
    return apiClient.get('/traceability/events', { params })
  },
  
  // Obtener historial de un producto
  getProductHistory(productId, params = {}) {
    return apiClient.get(`/traceability/products/${productId}/history`, { params })
  },
  
  // Obtener dashboard de trazabilidad
  getTraceabilityDashboard(params = {}) {
    return apiClient.get('/traceability/dashboard', { params })
  },
  
  // Crear evento de trazabilidad manual
  createTraceabilityEvent(eventData) {
    return apiClient.post('/traceability/events', eventData)
  },

  // ==================== INVENTARIO GRANULAR ====================
  
  // Obtener items disponibles por producto (lotes, S/N, fechas de vencimiento)
  getProductAvailableItems(productId) {
    return apiClient.get(`/inventory/products/${productId}/available-items`)
  },
  
  // Verificar disponibilidad de lote específico
  checkLotAvailability(productId, lotNumber) {
    return apiClient.get(`/inventory/products/${productId}/lot/${lotNumber}/availability`)
  },
  
  // Verificar disponibilidad de número de serie específico
  checkSerialAvailability(productId, serialNumber) {
    return apiClient.get(`/inventory/products/${productId}/serial/${serialNumber}/availability`)
  },
  
  // Obtener resumen de criterios disponibles para un producto
  getProductCriteriaSummary(productId) {
    return apiClient.get(`/inventory/products/${productId}/criteria-summary`)
  }
}
